import React, { useRef, useEffect, useMemo, useCallback } from "react";
import { DateRangeFilter } from "./filterTypes";
import SearchDateRangePicker from "./SearchDateRangePicker";
import { formatDateRangeDisplay, PhotoDateRange } from "../utils/dateRangeUtils";

interface DateRangeDropdownProps {
  value: DateRangeFilter;
  onChange: (updated: DateRangeFilter) => void;
  onClose: () => void;
  photoDateRange?: PhotoDateRange | null;
}

const toYMD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseYMD = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
  value,
  onChange,
  onClose,
  photoDateRange
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;

      if (ref.current?.contains(target)) return;

      if (target instanceof Element && target.closest(".mobile-filters-modal"))
        return;

      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const handleRangeChange = useCallback(
    (range: { from?: Date; to?: Date }) => {
      if (!range.from && !range.to) {
        onChange({ startDate: null, endDate: null });
        return;
      }

      const startDate = range.from ? toYMD(range.from) : null;
      const endDate = range.to ? toYMD(range.to) : null;
      onChange({ startDate, endDate });
    },
    [onChange]
  );

  const clear = useCallback(() => {
    onChange({ startDate: null, endDate: null });
  }, [onChange]);

  const formattedLabel = useMemo(
    () => formatDateRangeDisplay(value.startDate, value.endDate) || null,
    [value.startDate, value.endDate]
  );

  const pickerValue = useMemo(
    () => ({
      from: value.startDate ? parseYMD(value.startDate) : undefined,
      to: value.endDate ? parseYMD(value.endDate) : undefined
    }),
    [value.startDate, value.endDate]
  );

  return (
    <div
      className="search-filter-dropdown search-filter-dropdown--date"
      ref={ref}
    >
      <SearchDateRangePicker
        value={pickerValue}
        onChange={handleRangeChange}
        photoDateRange={photoDateRange}
      />
      <div className="search-filter-calendar__footer">
        {formattedLabel && (
          <span className="search-filter-calendar__selection">
            {formattedLabel}
          </span>
        )}
        <button className="search-filter-calendar__clear" onClick={clear}>
          Clear
        </button>
      </div>
    </div>
  );
};

export default DateRangeDropdown;
